import {REGISTRY_SINGLETON} from "./Registry";
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectGameType,
  selectHexes,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPendingShootingActions,
  selectPlayers,
  selectSharedGameState,
  selectSpecialBuildings,
  selectUnits
} from "../redux/reducers/sharedStateReducer/selectors";
import {checkIfParallelSupplyPhase, resolveSupplyActorId} from "../utils/gameState/supply/parallelSupply";
import {getBuildingPrice} from "../utils/economy";
import store from "../redux/store";
import {SOUND_TIMEOUT_AFTER_REROLL} from "../components/TokensToolbar/RerollButton/RerollButton";
import {
  setServerTimeDelta,
  showGoblinsTrapKillNotice,
  showPolicyPurchaseToast,
  showToast, withToastIcon
} from "../redux/actions";
import {getActingPlayer} from "../utils/players/getActingPlayer";
import {
  selectIsCityCaptureAnimationPlaying,
  selectIsRuinsIncomeAnimationAwaited,
  selectUserId,
} from "../redux/reducers/privateStateReducer/selectors";
import {checkIfUnitMeleeElf} from "../utils/factions/elves/checkIfUnitMeleeElf";
import {
  POLICY_FANATICISM_COINS_CONVERSION,
  POLICY_FANATICISM_WIN_POINTS_CONVERSION
} from "../configs/policy/fanaticism";
import {calcAgrarianismConversion} from "../utils/policy/agrarianism/calcAgrarianismConversion";
import {calcPolicyPrice} from "../utils/policy/calcPolicyPrice";
import {getCoinsActionText} from "../utils/gameState/text/getCoinsText";
import {playBuildingPlacementEffects, playSpecialBuildingPlacementEffects} from "./playBuildingPlacementEffects";
import {playUnitRecruitmentEffects, playUnitSizeRecruitmentEffects} from "./playUnitRecruitmentEffects";
import {
  getInitiativeActionRejection
} from "../utils/gameState/initiative/getInitiativeInterceptionRejection";
import {playUnitMovementAnimation} from "./playUnitMovementAnimation";
import {getShotTimeFactor, playShotAnimation} from "./playShotAnimation";
import {
  playGoblinsBuildingDamageEffects,
  playGoblinsTrapRemovalEffects,
  playGoblinsTrapSetupEffects,
  playLeprechaunsTheftEffects,
  playSuccubesSeductionEffects,
  playUnitUpgradeEffects,
} from "./playAbilityCastEffects";
import {SUCCUBES_SEDUCTION_COST} from "../configs/factions/succubes";
import {GOBLINS_TRAP_REMOVAL_REWARD, GOBLINS_TRAP_SETUP_COST} from "../configs/factions/goblins";
import {TOAST_ICONS} from "../constants/toast";

// Максимальная поправка серверного времени по одному сообщению смены хода:
// большее расхождение означает не дрейф часов, а устаревшее сообщение
const MAX_TURN_TIMESTAMP_DELTA_CORRECTION_MS = 60000;

/**
 * Игрок, за которого совершено действие снабжения или политики: явный
 * payload.playerId, если такой игрок есть в партии, иначе текущий игрок (все
 * поочередные партии и старые записи — там действует только ходящий игрок)
 */
const resolveActionPlayer = (gameState, payload, players, currentPlayer) => {
  const actorId = resolveSupplyActorId(gameState, payload);
  return (players || []).find((player) => player.id === actorId) || currentPlayer;
};

// prevState — все состояние до экшена: способности фракций придерживают по
// нему свой итог на поле до конца анимации (см. playAbilityCastEffects)
export const playEffectForServerAction = (action, prevPolicyMarket, prevHexes, prevPlanEntries, prevUnits, prevState) => {
  try {
    const soundManager = REGISTRY_SINGLETON.getSoundManager();
    const hexGridRenderer = REGISTRY_SINGLETON.getHexGridRenderer();
    const {type, payload} = action;

    const state = store.getState();
    const gameType = selectGameType(state);
    const userId = selectUserId(state);
    const currentPlayer = selectCurrentPlayer(state);
    const players = selectPlayers(state);
    const citiesCache = selectCitiesCache(state);
    const units = selectUnits(state);
    const hexes = selectHexes(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const nextInitiativePlayerId = selectNextInitiativePlayerId(state);
    const pendingShootingActions = selectPendingShootingActions(state);
    const pendingBuildings = selectPendingBuildings(state);

    const actingPlayer = getActingPlayer(gameType, userId, players, currentPlayer);

    // Одновременная фаза снабжения: снабжение раздают все игроки разом, и
    // звук раздачи на каждое чужое действие слился бы в сплошной треск. Звучат
    // только действия за своего действующего игрока (например, серверная
    // авто-раздача) — свои клики звучат на месте, сервер их не возвращает
    const sharedGameState = selectSharedGameState(state);
    const isParallelSupplyPhase = checkIfParallelSupplyPhase(sharedGameState);
    const checkIfOwnParallelSupplyAction = () => resolveSupplyActorId(sharedGameState, payload) === actingPlayer?.id;

    switch (type) {
      case 'DISTRIBUTE_SUPPLY':
        if (isParallelSupplyPhase && !checkIfOwnParallelSupplyAction()) {
          break;
        }
        // Авто-раздача, пришедшая во время заставки поля (облет захваченных
        // городов, рост дохода руин), — без звука: снабжение на поле покажется
        // только после нее
        if (selectIsCityCaptureAnimationPlaying(state) || selectIsRuinsIncomeAnimationAwaited(state)) {
          break;
        }
        soundManager.playDistributeSupply();
        break;
      case 'REPAIR_BUILDING':
        soundManager.playBuildingRepair();
        break;
      case 'SET_HEX_TOKEN': {
        const {tokenType, price, key} = payload;

        // Рост города и доход затронутых соседей видят все участники матча,
        // а не только строящий. Звук постройки, пыль и число потраченных монет
        // — в момент, когда жетон коснется земли; без поля воронка сыграет
        // звук сразу
        playBuildingPlacementEffects(store, soundManager, hexGridRenderer, key, prevHexes, prevPlanEntries, {
          playPlacementSound: () => soundManager.playBuildingPlacement(tokenType, price),
          price,
          isPaced: true,
        });
        break;
      }
      case 'SET_SPECIAL_BUILDING':
        const {hexKey, buildingType} = payload;

        const {priceCoins} = getBuildingPrice(buildingType, currentPlayer, pendingBuildings);
        // Прилет жетона, звук постройки, пыль и число потраченных монет видят
        // все участники матча, а не только строящий
        playSpecialBuildingPlacementEffects(store, hexGridRenderer, hexKey, buildingType, {
          playPlacementSound: () => soundManager.playBuildingPlacement(buildingType, priceCoins),
          price: priceCoins,
          isPaced: true,
        });
        if (hexGridRenderer) {
          setTimeout(() => {
            hexGridRenderer.showSpecialBuildingIncomeAnimation(
              hexKey, buildingType, players, citiesCache, units, hexes, specialBuildings, buildingsDamage, currentPlayer
            );
          }, 1000);
        }
        break;
      case 'TOGGLE_INITIATIVE_INTERCEPTION': {
        // Состояние читается уже ПОСЛЕ применения экшена, а отвергнутый
        // правилами перехват движок игнорирует — выглядит он тогда как отмена.
        // Поэтому «перехватил» говорим, только если перехват действительно
        // стал его, а «отменил» — только если этот же экшен сейчас был бы
        // законным: иначе соперники видели бы «X отменил перехват инициативы»
        // на чужую незаконную попытку
        if (nextInitiativePlayerId === currentPlayer.id) {
          store.dispatch(withToastIcon(showToast(`${currentPlayer.name} перехватил инициативу!\n\nОн будет ходить первым в фазе Войны.`, 'success', 5000, true), TOAST_ICONS.INITIATIVE));
          soundManager.playInitiativeInterception();
        } else if (!nextInitiativePlayerId && !getInitiativeActionRejection(sharedGameState, action)) {
          store.dispatch(withToastIcon(showToast(`${currentPlayer.name} отменил перехват инициативы.`, 'info'), TOAST_ICONS.INITIATIVE));
        }
        break;
      }
      case 'RECRUIT_UNIT': {
        const {unitId, hexKey, price, unitType, isRatmenForSupplyRecruitment} = payload;

        // Новобранца крысолюдов за снабжение показывает свой middleware: откуда бы он
        // ни взялся (кнопка, авто-раздача) и кому бы ни был виден (см.
        // supplyRecruitAnimationMiddleware)
        if (isRatmenForSupplyRecruitment) {
          break;
        }

        // Проявление отряда, звук найма, волну цветом игрока и число потраченных
        // монет видят все участники матча, а не только нанявший.
        //
        // Эффект висит именно на этом экшене, хотя найм завершает следующий
        // (ADD_PENDING_RECRUITMENT_ACTION): отряд появляется в состоянии ровно
        // здесь, и спрятать его до проявления надо в той же задаче, до первой
        // отрисовки. На соседнем экшене отряд успевал мелькнуть уже стоящим на
        // казарме — в записи матча и у зрителей эти два экшена приходят
        // отдельно, с паузой между ними
        const recruitedUnit = units.find((unit) => unit.id === unitId) || null;
        playUnitRecruitmentEffects(store, hexGridRenderer, {
          unitId,
          hexKey,
          price,
          isPaced: true,
          // Нанятый отряд звучит по своему облику — как и у нанявшего его игрока
          playRecruitmentSound: () => soundManager.playUnitRecruitment(
            unitType, recruitedUnit?.ownerId, actingPlayer, price, recruitedUnit
          ),
          playCastSound: () => soundManager.playUnitRecruitmentCast?.(unitType),
          playImpactSound: () => soundManager.playUnitRecruitmentImpact?.(unitType),
        });
        break;
      }
      case 'ADD_PENDING_RECRUITMENT_ACTION': {
        const {actionType, price, unitId, quarterHexKey, sizeAdded} = payload;

        // Появление нового отряда — на RECRUIT_UNIT выше; здесь остается только
        // прибавка размера уже стоящему отряду
        if (actionType === 'SIZE_RECRUITMENT') {
          soundManager.playCoins(price)
          // Подсветку ячейки под отрядом и число потраченных монет над ним
          // видят все участники матча
          playUnitSizeRecruitmentEffects(store, hexGridRenderer, {
            unitId,
            quarterHexKey,
            amount: sizeAdded,
            price,
            isPaced: true,
            playTransferSound: () => soundManager.playUnitSizeTransfer?.(),
          });
        }
        break;
      }
      case 'MOVE_UNIT': {
        const {unitId, unitType} = payload;

        // Свой звук хода у отряда с образом фракции (волки, крысы, великаны)
        // решает сам отряд — берем его из состояния до хода
        const movedUnit = (prevUnits || []).find((unit) => unit.id === unitId) || null;
        soundManager.playUnitMovement(unitType, movedUnit);
        // Чужой ход меняет состояние сразу, поэтому отряд ведет по полю
        // двойник — иначе отряд соперника переставлялся бы мгновенно
        playUnitMovementAnimation(store, hexGridRenderer, {unitId, prevUnits});
        break;
      }
      case 'ADD_PENDING_SHOOTING_ACTION': {
        const {id, shooterUnitId, targetUnitId, isBadLuck} = payload;

        const shooterUnit = units.find(unit => unit.id === shooterUnitId);
        const isUnitMeleeElf = shooterUnit && checkIfUnitMeleeElf(shooterUnit)

        // Чужой выстрел меняет состояние сразу — залп летит следом, а итог
        // появляется на поле с попаданием. Темп здесь чужой: у бота и соперника
        // залп укладывается в шаг до следующего действия, а запись матча ждет
        // конца залпа сама (см. ReplayRunner)
        const isShotAnimated = playShotAnimation(store, hexGridRenderer, {actionId: id, isPaced: true});
        // Звук идет за залпом: сжатый под шаг темпа залп попадает раньше — и
        // удар звучит тогда же
        soundManager.playShooting(
          isUnitMeleeElf, !!targetUnitId, isBadLuck, isShotAnimated ? getShotTimeFactor(hexGridRenderer, id) : 1
        );
        break;
      }
      case 'REROLL_PENDING_SHOOTING_ACTION': {
        const {id, isUnitTarget, isBadLuck, isDice1Selected, isDice2Selected} = payload;

        const rerolledShootingAction = pendingShootingActions.find(action => action.id === id);
        if (!rerolledShootingAction) {
          break;
        }

        const shooterUnit = units.find(unit => unit.id === rerolledShootingAction.shooterUnitId);
        const isUnitMeleeElf = shooterUnit && checkIfUnitMeleeElf(shooterUnit)

        // Переброс — выстрел заново: залп уходит вместе со звуком выстрела,
        // после костей, а прежний итог до попадания с поля убран
        const isShotAnimated = playShotAnimation(store, hexGridRenderer, {
          actionId: id, delayMs: SOUND_TIMEOUT_AFTER_REROLL, isReroll: true, isPaced: true,
        });
        // Залп, сжатый под шаг темпа, уходит и попадает раньше: пауза на
        // кости и звуки выстрела сжимаются вместе с ним
        const timeFactor = isShotAnimated ? getShotTimeFactor(hexGridRenderer, id) : 1;

        soundManager.playRerollDices(isDice1Selected, isDice2Selected)
        setTimeout(() => {
          soundManager.playShooting(isUnitMeleeElf, isUnitTarget, isBadLuck, timeFactor);
        }, Math.round(SOUND_TIMEOUT_AFTER_REROLL * timeFactor))
        break;
      }
      case 'CALCULATE_MELEE_COMBAT_DAMAGE': {
        const {
          priorityTargetForUnit, isReroll, isBadLuck, isDice1Selected, isDice2Selected, isDice3Selected, isDice4Selected,
        } = payload;

        if (isReroll) {
          soundManager.playRerollDices(isDice1Selected, isDice2Selected, isDice3Selected, isDice4Selected)
          setTimeout(() => {
            if (isBadLuck) {
              soundManager.playRerollBadLuck();
            } else {
              soundManager.playMeleeCombatAfterReroll();
            }
          }, SOUND_TIMEOUT_AFTER_REROLL)
        } else if (priorityTargetForUnit) {
          soundManager.playPriorityTargetSelect();
        } else {
          soundManager.playMeleeCombat();
        }
        break;
      }
      case 'UNIT_UPGRADE': {
        // Золотые шевроны, звук и потраченные монеты видят все участники матча
        const {unitId, newType, price} = payload;
        playUnitUpgradeEffects(store, hexGridRenderer, soundManager, {unitId, newType, price, prevState, isPaced: true});
        break;
      }
      // FACTIONS
      case 'FACTION_LEPRECHAUNS_STEAL_COINS': {
        const {unitId, playerId, coinsStolen} = payload
        const player = players.find(player => player.id === playerId);
        let coinsText = getCoinsActionText(coinsStolen);
        const isActingPlayerRobbed = actingPlayer.id === playerId;
        const toastType = isActingPlayerRobbed ? 'error' : 'info';
        const toastMessage = isActingPlayerRobbed ?
          `${currentPlayer.name} украл у Вас ${coinsStolen} ${coinsText}!`
          : `${currentPlayer.name} украл ${coinsStolen} ${coinsText} у ${player.name}`
        store.dispatch(withToastIcon(showToast(toastMessage, toastType, 3000, true, true), TOAST_ICONS.STEAL));
        // Звук кражи и «+N» украденных монет над отрядом-вором
        playLeprechaunsTheftEffects(store, hexGridRenderer, soundManager, {unitId, coinsStolen, isPaced: true});
        break;
      }
      case 'FACTION_SUCCUBES_SEDUCE_UNIT': {
        const {unitId, targetUnitId, previousOwnerId, success} = payload;
        const previousOwner = players.find(player => player.id === previousOwnerId);
        const targetDescription = previousOwner ? `отряд игрока ${previousOwner.name}` : 'нейтральный отряд';
        const isActingPlayerRobbed = Boolean(previousOwner) && actingPlayer.id === previousOwnerId;
        const seducerName = currentPlayer.name;

        // Сердце летит к цели, итог (тост, звук, смена хозяина на поле) — в
        // момент попадания
        playSuccubesSeductionEffects(store, hexGridRenderer, soundManager, {
          unitId,
          targetUnitId,
          success,
          cost: SUCCUBES_SEDUCTION_COST,
          prevState,
          isPaced: true,
          onImpact: () => {
            if (success) {
              const toastType = isActingPlayerRobbed ? 'error' : 'info';
              const toastMessage = isActingPlayerRobbed
                ? `${seducerName} соблазнил(а) один из Ваших отрядов!`
                : `${seducerName} соблазнил(а) ${targetDescription}!`;
              store.dispatch(showToast(toastMessage, toastType, 3000, true, true));
            } else {
              store.dispatch(showToast(
                `${seducerName} попытался(ась) соблазнить ${targetDescription}, но неудачно.`,
                'info', 3000, true, true
              ));
            }
          },
        });
        break;
      }
      case 'FACTION_GOBLINS_SET_TRAP': {
        // Ловушка невидима для всех, кроме владельца (см. add-goblins-faction
        // design.md Decision 1) — текст тоста намеренно НЕ содержит гекс цели,
        // а анимация показывает ячейку только владельцу
        const {unitId, trapId, hexKey, success} = payload;
        const trapperName = currentPlayer.name;
        playGoblinsTrapSetupEffects(store, hexGridRenderer, soundManager, {
          unitId,
          trapId,
          hexKey,
          ownerId: currentPlayer.id,
          success,
          cost: GOBLINS_TRAP_SETUP_COST,
          prevState,
          isPaced: true,
          onImpact: () => store.dispatch(withToastIcon(showToast(
            success
              ? `${trapperName} успешно установил ловушку.`
              : `${trapperName} попытался(ась) установить ловушку, но неудачно.`,
            'info', 3000, true, true
          ), TOAST_ICONS.TRAP)),
        });
        break;
      }
      case 'FACTION_GOBLINS_REMOVE_TRAP': {
        store.dispatch(withToastIcon(showToast(`${currentPlayer.name} снял(а) свою ловушку.`, 'info', 3000, true, true), TOAST_ICONS.TRAP));
        playGoblinsTrapRemovalEffects(store, hexGridRenderer, soundManager, {
          unitId: payload.unitId, reward: GOBLINS_TRAP_REMOVAL_REWARD, isPaced: true,
        });
        break;
      }
      case 'FACTION_GOBLINS_DAMAGE_BUILDING': {
        // destroyed/buildingLabel/victimPlayerId посчитаны атакующим клиентом
        // ДО диспатча (см. GameScene.js) - к моменту этого эффекта
        // specialBuildings/hexes по этому гексу уже могли измениться, взять их
        // здесь из пост-диспатчевого состояния было бы нечем (аналогично
        // ownerId у APPLY_GOBLINS_TRAP_MOVEMENT ниже)
        const {unitId, hexKey, success, destroyed, damageIncrement, buildingLabel, victimPlayerId} = payload;
        const isActingPlayerVictim = Boolean(victimPlayerId) && actingPlayer.id === victimPlayerId;
        const bomberName = currentPlayer.name;

        // Бомба летит к строению, итог — в момент попадания
        playGoblinsBuildingDamageEffects(store, hexGridRenderer, soundManager, {
          unitId,
          hexKey,
          success,
          destroyed: Boolean(destroyed),
          damageIncrement,
          prevState,
          isPaced: true,
          onImpact: () => {
            if (success) {
              const toastType = isActingPlayerVictim ? 'error' : 'info';
              const targetDescription = isActingPlayerVictim ? 'ваше' : 'вражеское';
              const verb = destroyed ? 'разрушил(а)' : 'повредил(а)';
              store.dispatch(showToast(
                `${bomberName} ${verb} ${targetDescription} строение: ${buildingLabel}.`,
                toastType, 3000, true, true
              ));
            } else {
              store.dispatch(showToast(`${bomberName} попытался(ась) повредить строение, но неудачно.`, 'info', 3000, true, true));
            }
          },
        });
        break;
      }
      case 'APPLY_GOBLINS_TRAP_MOVEMENT': {
        // Ловушка уже раскрыта фактом срабатывания - гекс здесь можно
        // показывать всем, в отличие от FACTION_GOBLINS_SET_TRAP выше.
        // ownerId/finalHexKey/unitType/unitSizeBeforeDamage берутся из
        // payload, а не из пост-диспатчевого state.units - юнит уже мог быть
        // удалён из units, если погиб (см. reducer-кейс), а тип/размер до
        // урона нужны для отрисовки индикаторов/призрака (design.md
        // Decision 21).
        //
        // Индикаторы на поле НЕ создаются здесь напрямую (в отличие от
        // прежней версии) - вместо этого диспатчится то же состояние
        // показа, что и на клиенте, совершившем ход (showGoblinsTrapKillNotice,
        // blocking: false здесь - у наблюдателя нет клика, снимается таймером),
        // а рисует его каждый рендер-такт HexGridRenderer.updateGoblinsTrapTriggerDisplay
        // - персистентный редрав устраняет и порядковую зависимость от
        // dispatch(showToast(...)), и баг с накладывающимися гексами (Decision 20/21)
        const {
          unitId, unitDied, triggeredTrapEvents, ownerId, finalHexKey, unitType, unitSizeBeforeDamage, remainingSize,
          unitFactionId, unitCapturedFromOwnerId
        } = payload;

        if (triggeredTrapEvents && triggeredTrapEvents.length > 0) {
          const victimOwner = players.find(p => p.id === ownerId) || null;
          const isActingPlayerVictim = Boolean(victimOwner) && actingPlayer.id === victimOwner.id;
          const victimName = victimOwner ? victimOwner.name : '';
          const toastMessage = unitDied
            ? (isActingPlayerVictim
              ? 'Ваш отряд попался в ловушку гоблинов и погиб!'
              : `Отряд игрока ${victimName} попался в ловушку гоблинов и погиб!`)
            : (isActingPlayerVictim
              ? 'Ваш отряд попал в ловушку гоблинов!'
              : `Отряд игрока ${victimName} попал в ловушку гоблинов!`);
          store.dispatch(withToastIcon(showToast(toastMessage, isActingPlayerVictim ? 'error' : 'info', 3500, true, true), TOAST_ICONS.TRAP));

          // Реальная потеря численности (см. GameScene.js, тот же фикс) -
          // не сумма сырого урона по ловушкам, у Орков это разные числа
          const totalDamageTaken = unitSizeBeforeDamage - remainingSize;
          store.dispatch(showGoblinsTrapKillNotice({
            // factionId/capturedFromOwnerId - чтобы призрак (resolveUnitImageKey)
            // корректно показал купленный фракционный скин, а не общий силуэт
            // (найдено пользователем в ручном тестировании). id обязателен -
            // HexGridRenderer.renderUnit сверяет его с unit.id, чтобы решить,
            // показывать ли численность ДО урона рядом с "-N" (см. design.md);
            // без него сверка не проходила никогда, и наблюдатели видели уже
            // уменьшенную численность одновременно с индикатором потерь
            // (найдено внешним ревью ветки add-goblins-faction)
            unit: {
              id: unitId, type: unitType, ownerId, size: unitSizeBeforeDamage,
              factionId: unitFactionId, capturedFromOwnerId: unitCapturedFromOwnerId
            },
            died: unitDied,
            finalHexKey,
            totalDamageTaken,
            triggeredTrapEvents,
            blocking: false,
          }));
          // Таймера скрытия здесь нет: индикаторы срабатывания на поле живут
          // до конца этапа перемещения/маневра (сброс по
          // COMPLETE_PHASE/SET_WAR_PHASE_STAGE в goblinsTrapKillNoticeReducer)
          soundManager.playGoblinsTrapTrigger(triggeredTrapEvents);
        }
        break;
      }
      // POLICIES
      case 'BUY_POLICY': {
        const {index} = payload;

        // Плашка с иконкой фракции, ником и названием политики вместо строки
        // текста (MatchPolicyToast) — такая же, как на витрине главного меню
        const policy = prevPolicyMarket[index];
        store.dispatch(showPolicyPurchaseToast(currentPlayer.id, policy));
        // Покупатель — текущий игрок: покупка ход не передает, а цена зависит
        // только от места политики на рынке и фракции покупателя
        soundManager.playBuyPolicy(calcPolicyPrice(index, currentPlayer));
        break;
      }
      case 'CLEAR_POLICY_MARKET': {
        const toastMessage = `${currentPlayer.name} обновил рынок политик.`;
        store.dispatch(showToast(toastMessage, 'info', 3000, true));
        soundManager.playClearPolicyMarket();
        break;
      }
      case 'POLICY_FANATICISM_CONVERT_WIN_POINTS': {
        // Обменявший — игрок из payload.playerId: в одновременной фазе
        // снабжения это не текущий (замороженный) игрок
        const convertingPlayer = resolveActionPlayer(sharedGameState, payload, players, currentPlayer);
        // «Вы обменяли» — только в одновременной фазе снабжения: поочередные
        // партии сохраняют прежний текст
        const playerText = isParallelSupplyPhase && convertingPlayer.id === actingPlayer?.id
          ? 'Вы обменяли'
          : `${convertingPlayer.name} обменял`;
        store.dispatch(withToastIcon(showToast(`${playerText} ${POLICY_FANATICISM_WIN_POINTS_CONVERSION} очка на ${POLICY_FANATICISM_COINS_CONVERSION} монет\n(способность политики Фанатизм).`, 'info', 3000, true, true), TOAST_ICONS.COINS));
        soundManager.playCoins(POLICY_FANATICISM_COINS_CONVERSION);
        break;
      }
      case 'POLICY_AGRARIANISM_SELECT_FARM_FOR_CONVERSION': {
        if (isParallelSupplyPhase && !checkIfOwnParallelSupplyAction()) {
          break;
        }
        soundManager.playDistributeSupply();
        break;
      }
      case 'POLICY_AGRARIANISM_COMMIT_CONVERSION': {
        const {selectedFarmsForConversion} = payload;
        const {coins, usedSupplyCount} = calcAgrarianismConversion(selectedFarmsForConversion);
        // Обменявший — игрок из payload.playerId (в одновременной фазе
        // снабжения обмен при закрытии фазы идет за каждого игрока), иначе
        // текущий игрок
        const convertingPlayer = resolveActionPlayer(sharedGameState, payload, players, currentPlayer);

        let playerText
        if (convertingPlayer.id === actingPlayer.id) {
          playerText = 'Вы обменяли'
        } else {
          playerText = `${convertingPlayer.name} обменял`
        }
        let coinsText = getCoinsActionText(coins);

        store.dispatch(withToastIcon(showToast(`${playerText} ${usedSupplyCount} снабжения на ${coins} ${coinsText}\n(способность политики Аграризм).`, 'info', 5000, true, true), TOAST_ICONS.COINS));
        soundManager.playCoins(coins);
        break;
      }
      case 'POLICY_ISOLATIONISM_INTERCEPT_INITIATIVE': {
        // Бесплатный перехват тоже проверяется правилами: об отвергнутом
        // молчим, иначе в партии объявили бы перехват, которого не было
        if (nextInitiativePlayerId === currentPlayer.id) {
          store.dispatch(withToastIcon(showToast(`${currentPlayer.name} перехватил инициативу\n при покупке политики Изоляционизм!\n\nОн будет ходить первым в фазе Войны.`, 'success', 5000, true), TOAST_ICONS.INITIATIVE));
          soundManager.playInitiativeInterception();
        }
        break;
      }
      case 'SEND_CONSTRUCTION_VALIDATION_MESSAGE': {
        if (currentPlayer.id === actingPlayer.id) {
          const {validationMessage} = payload;

          store.dispatch(showToast(validationMessage, 'error', 15000, false, true));
        }
        break;
      }
      case 'SET_TURN_END_TIMESTAMP': {
        const {timestamp} = payload;
        // Корректируем разницу между локальным временем устройства и серверным.
        // Это позволяет синхронизировать отображение таймера на разных клиентах.
        // Калибровка предполагает, что сообщение обработано сразу по получении.
        // Сообщение, разобранное с большим опозданием (буфер приостановленной/
        // фоновой вкладки при возврате игрока), сдвинуло бы дельту на произвольную
        // величину и сломало таймер до следующей смены хода — большие расхождения
        // игнорируем: стартовой синхронизацией через /api/time владеет App
        const newDelta = timestamp - Date.now();
        const currentDelta = store.getState().private?.serverTime?.serverTimeDelta || 0;
        if (Math.abs(newDelta - currentDelta) <= MAX_TURN_TIMESTAMP_DELTA_CORRECTION_MS) {
          store.dispatch(setServerTimeDelta(newDelta));
        }
        break;
      }
      default:
    }
  } catch (error) {
    console.error('[playEffectForServerAction] Error:', error);
  }
}